import {Task} from '../../models/dashboard-models';
import React, {useContext, useState} from 'react';
import {resourceCache, useAsyncResource} from 'use-async-resource';
import {useTranslation} from 'react-i18next';
import {Filter} from '../../models/table-models';
import {addInvoices, useGetBranchPaymentInfo, useGetInvoices} from '../../utils/clients-hooks';
import {Button, FormLabel} from 'react-bootstrap';
import {CustomButton} from '../CustomButton';
import {faPlus} from '@fortawesome/free-solid-svg-icons';
import {faCheck} from '@fortawesome/free-solid-svg-icons/faCheck';
import {BranchPaymentData} from '../NewRequest/NewRequestInvoices';
import {TasksContext} from '../../context/TasksContext';
import {completeTaskAsCoordinator} from '../../utils/dashboard-hooks';
import {toast} from 'react-toastify';
import {CustomToastContent} from '../CustomToastContent';
import {toastOptionsTopRight} from '../../utils/Enums/ToastOptions';
import {StepWizardChildProps} from 'react-step-wizard';
import {useGetReferences, useGetReferencesForDistributor} from '../../utils/warehouses-hooks';
import {Role} from "../../utils/Enums/Roles";
import {AuthContext} from "../../context/AuthContext";
import {Invoices} from "../Dashboard/Invoices";

interface CustomStepWizardChildProps extends Partial<StepWizardChildProps> {
    refetchTasks: () => void;
    closeModal: () => void;
}

export const PaymentsStep: React.FC<CustomStepWizardChildProps> = ({
    previousStep,
    refetchTasks,
    closeModal,
}: CustomStepWizardChildProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const {
        selectedTask,
        taskDevices,
        usedItems,
        materials,
        attachBatchNumbersToDevices,
        setSelectedTask,
        setTaskDevices,
        setUsedItems,
    } = useContext(TasksContext);
    const { branchVO } = selectedTask as Task;
    const devices = [...taskDevices].concat();
    const [unpaidReason, setUnpaidReason] = useState('');
    const [getUnPaidInvoices, fetchNewInvoices] = useAsyncResource(useGetInvoices, {
        filter: {
            id: selectedTask?.branchId ? String(selectedTask?.branchId) : undefined,
        } as Filter,
    },false);
    const { unpaidInvoices } = getUnPaidInvoices();

    const [getPaidInvoices, fetchNewPaidInvoices] = useAsyncResource(useGetInvoices, {
        filter: {
            id: selectedTask?.branchId ? String(selectedTask?.branchId) : undefined,
        } as Filter,
    },true);
    const { paidInvoices } = getPaidInvoices();

    const { hasPermission } = useContext(AuthContext);
    const isDistributor = !hasPermission([Role.COORDINATOR]) && (hasPermission([Role.DISTRIBUTOR]) || hasPermission([Role.INSTALLER]));

    const fetchHook = () => {
        fetchNewPaidInvoices({
            filter: {
                id: selectedTask?.branchId ? String(selectedTask?.branchId) : undefined,
            } as Filter,
        },true);
        fetchNewInvoices({
            filter: {
                id: selectedTask?.branchId ? String(selectedTask?.branchId) : undefined,
            } as Filter,
        });
    };

    const addInvoice = async () => {
        await addInvoices(selectedTask?.branchId);
        fetchHook();
    };

    const [readBranchPaymentInfo, getBranchPaymentInfo] = useAsyncResource(useGetBranchPaymentInfo, selectedTask?.branchId);
    const branchPaymentInfo = readBranchPaymentInfo();

    const handleCompleteTask = async () => {
        attachBatchNumbersToDevices();
        const response = await completeTaskAsCoordinator(selectedTask, { devices: taskDevices,materials,usedItems }, false);
        if (response) {
            setSelectedTask(undefined);
            setTaskDevices([]);
            setUsedItems([]);
            toast.success(<CustomToastContent content={[`${t('taskCompleted')}`]} />, toastOptionsTopRight);
            refetchTasks();
            closeModal();
            resourceCache(useGetReferencesForDistributor).clear();
            resourceCache(useGetReferences).clear();
            resourceCache(useGetBranchPaymentInfo).clear();
            resourceCache(useGetInvoices).clear();
        }
    };

    return (
        <div className='p-3'>
            <div className='scrollable-container w-100 '>
                <div className='mt-4 d-flex align-items-center'>
                    {!isDistributor && <div className='pt-5 mx-5'>
                        <CustomButton
                            onClick={addInvoice}
                            icon={faPlus}
                            title={t('addInvoice')}
                            iconClass='me-2'
                            className={`btn-success text-nowrap outline-none px-4 `}
                        />
                        <div className={'my-3'}>
                            <FormLabel>
                                {t('unpaidReason')}:
                                <textarea
                                    value={unpaidReason}
                                    inputMode='text'
                                    className='w-100 p-2 my-2'
                                    onChange={(e) => setUnpaidReason(e.currentTarget.value)}
                                    disabled={unpaidInvoices.length === 0}
                                    placeholder={t('unpaidReason')}
                                />
                            </FormLabel>
                        </div>
                    </div>}

                    <Invoices fetchHook={fetchHook} paidInvoices={paidInvoices} unpaidInvoices={unpaidInvoices}/>
                </div>
                <BranchPaymentData
                    selectedBranch={{ ...branchVO, imgUrl: '', activity: '', isCompany: true}}
                    {...{
                        branchPaymentInfo,
                        devices,
                        materials,
                        getBranchPaymentInfo,
                        deviceInstallation:selectedTask?.taskCategory === 'INSTALLATION',
                        viewOnly:true,
                    }}
                />
            </div>
            <div className='d-flex pt-3 justify-content-between'>
                <Button onClick={() => previousStep!()}>{t('previous')}</Button>
                <CustomButton
                    onClick={handleCompleteTask}
                    icon={faCheck}
                    title={t('completeTask')}
                    iconClass='me-2'
                    className={`btn-success text-nowrap outline-none px-4 `}
                />
            </div>
        </div>
    );
};
